import AddIcon from "@mui/icons-material/Add"
import RefreshIcon from "@mui/icons-material/Refresh"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import {
  MonthlyExtractRunListItemDto,
  TriggerResultDto,
  useGetMonthlyExtractRunsQuery,
} from "src/features/things/monthlyExtractApi"
import { GenerateExtractDialog } from "./monthlyReports/GenerateExtractDialog"
import {
  fmtDuration,
  fmtPeriod,
  isActive,
  MonthlyRunStatusChip,
  MonthlyRunWindowChip,
  runWindow,
  RUN_POLL_MS,
  shareExpiry,
} from "./monthlyReports/monthlyReportsChrome"
import { fmtLocalDateTime, fmtNum } from "./reservationSync/ReservationSyncChrome"
import SaLayout from "./SaLayout"

/** A `queued` trigger (no run recorded yet) keeps the list polling this long. */
const QUEUED_POLL_WINDOW_MS = 15 * 60_000

/**
 * Every monthly GJC extract run, scheduled or on demand (LOCKER-469 DoD1), and
 * the Generate action (DoD3). Runs are append-only: a re-run of a month is a new
 * row beside the old one (N3).
 */
export const SaMonthlyReportsPage: FC = () => {
  const navigate = useNavigate()
  const [generateOpen, setGenerateOpen] = useState(false)
  const [lastTrigger, setLastTrigger] = useState<TriggerResultDto | null>(null)
  // Kept apart from the alert: dismissing the notice must not stop following a queued run.
  const [queuedAt, setQueuedAt] = useState<number | null>(null)

  const { data: firstRead } = useGetMonthlyExtractRunsQuery()
  const anyActive = (firstRead?.data ?? []).some((r) => isActive(r.status))
  const waitingOnQueued = queuedAt !== null && Date.now() - queuedAt < QUEUED_POLL_WINDOW_MS
  const { data, isLoading, isFetching, error, refetch } = useGetMonthlyExtractRunsQuery(undefined, {
    pollingInterval: anyActive || waitingOnQueued ? RUN_POLL_MS : 0,
  })

  const runs = useMemo(() => data?.data ?? [], [data])
  const periods = useMemo(() => new Set(runs.map((r) => r.period)), [runs])

  return (
    <SaLayout selectedMenuItemLabelKey="monthlyReports">
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 3, md: 5 }, py: { xs: 4, md: 5 } }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontSize: "1.3rem", fontWeight: 600, flexGrow: 1 }}>
            Monthly reports
          </Typography>
          <Tooltip title="Re-read the runs">
            <IconButton
              size="small"
              aria-label="refresh the run list"
              disabled={isFetching}
              onClick={() => void refetch()}
            >
              {isFetching ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setGenerateOpen(true)}>
            Generate
          </Button>
        </Stack>
        <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: "80ch", mb: 3 }}>
          The GJC monthly extract (OLAP v4). One row per run — the 1st-of-month schedule (one
          utc and one toronto run) and every on-demand run. Each run archives two workbooks — the
          GJC extract and the all-sites occupation. Nothing is shared until you open a run and
          create a share link for a report: a PIN-protected download, valid 30 days.
        </Typography>

        {lastTrigger && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            onClose={() => setLastTrigger(null)}
            data-testid="generate-result"
          >
            {lastTrigger.runId
              ? `Extract for ${fmtPeriod(lastTrigger.period)} (${lastTrigger.window}) started — run ${lastTrigger.runId}.`
              : `Extract for ${fmtPeriod(lastTrigger.period)} (${lastTrigger.window}) requested. It is queued behind the running extract and appears here when it starts.`}
          </Alert>
        )}
        {isLoading && <Typography variant="body2">loading runs…</Typography>}
        {!!error && (
          <Typography variant="body2" sx={{ color: "error.main" }}>
            could not load the runs
          </Typography>
        )}

        <Table size="small" data-testid="monthly-runs-table">
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell>Window</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Trigger</TableCell>
              <TableCell>Requested by</TableCell>
              <TableCell>Started</TableCell>
              <TableCell align="right">Duration</TableCell>
              <TableCell>Rows per tab</TableCell>
              <TableCell>Shared reports</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((r) => (
              <RunRow
                key={r.id}
                run={r}
                onOpen={() => navigate(`/sa/monthly-reports/${encodeURIComponent(r.id)}`)}
              />
            ))}
            {!isLoading && !runs.length && (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: "center", py: 5 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No runs yet. The schedule runs at 03:00 Toronto on the 1st; Generate
                    runs any month now.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <GenerateExtractDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        existingPeriods={periods}
        onTriggered={(res) => {
          setLastTrigger(res)
          setQueuedAt(res.runId === null ? Date.now() : null)
        }}
      />
    </SaLayout>
  )
}

const RunRow: FC<{ run: MonthlyExtractRunListItemDto; onOpen: () => void }> = ({ run, onOpen }) => {
  const shares = Object.values(run.shares ?? {})
  // The soonest expiry is the one an admin must act on first.
  const soonest = shares.map((s) => s.shareExpiresAt).sort()[0]
  const expiry = shareExpiry(soonest)
  return (
    <TableRow hover sx={{ cursor: "pointer" }} onClick={onOpen} data-testid={`run-row-${run.id}`}>
      <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{fmtPeriod(run.period)}</TableCell>
      <TableCell>
        <MonthlyRunWindowChip window={runWindow(run)} />
      </TableCell>
      <TableCell sx={{ maxWidth: 360 }}>
        <MonthlyRunStatusChip status={run.status} />
        {run.status === "failed" && run.error && (
          <Typography
            variant="caption"
            component="div"
            sx={{ color: "error.main", mt: 0.5, wordBreak: "break-word" }}
            data-testid="run-error"
          >
            {run.error}
          </Typography>
        )}
      </TableCell>
      <TableCell>{run.trigger}</TableCell>
      <TableCell sx={{ fontSize: "0.8rem" }}>{run.requestedBy}</TableCell>
      <TableCell sx={{ fontSize: "0.72rem", fontFamily: "monospace", whiteSpace: "nowrap" }}>
        {fmtLocalDateTime(run.startedAt ?? run.createdAt)}
      </TableCell>
      <TableCell align="right" sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
        {fmtDuration(run.durationMs)}
      </TableCell>
      <TableCell sx={{ fontSize: "0.72rem" }}>
        {run.rowCounts
          ? Object.entries(run.rowCounts).map(([tab, n]) => (
              <div key={tab}>
                {tab}: <strong>{fmtNum(n)}</strong>
              </div>
            ))
          : "—"}
      </TableCell>
      <TableCell
        sx={{ fontSize: "0.8rem", whiteSpace: "nowrap", color: expiry.expired ? "error.main" : undefined }}
        data-testid="run-shares"
      >
        {shares.length ? `${shares.length} · expires ${expiry.label}` : "—"}
      </TableCell>
    </TableRow>
  )
}

export default SaMonthlyReportsPage
